import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import { CommoditySelector } from "../components/CommoditySelector";

interface ChargeLayerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (commodityId: number | null, layerNumber: number | null) => void;
  isLoading: boolean;
  title: string;
  chargeId: number | null;
  layers: ChargeLayer[];
}

const NEW_LAYER = "new";

export const ChargeLayerModal = ({
  open,
  onClose,
  onSave,
  isLoading,
  title,
  layers,
}: ChargeLayerModalProps) => {
  const [commodityId, setCommodityId] = useState<number | null>(null);
  const [layerNumber, setLayerNumber] = useState<number | null>(null);
  const [showError, setShowError] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setCommodityId(null);
      setLayerNumber(null);
      setShowError(false);
    }
  }, [open]);

  const layerNumbers = useMemo(
    () =>
      [...new Set(layers.map((layer) => layer.LayerNumber))].sort(
        (a, b) => b - a,
      ),
    [layers],
  );

  const newLayerNumber = (layerNumbers[0] ?? 0) + 1;

  const getCommodityIdsInLayer = (number: number) =>
    layers
      .filter((layer) => layer.LayerNumber === number)
      .map((layer) => layer.CommodityId)
      .filter((id): id is number => id != null);

  const handleSave = () => {
    if (!commodityId) {
      setShowError(true);
      return;
    }

    onSave(commodityId, layerNumber);
    // Modal is closed by the parent via onSuccess callback
  };

  const handleCommodityChange = (id: number | null) => {
    setCommodityId(id);
    if (id) setShowError(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "20%",
            maxWidth: "700px",
            borderRadius: "12px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: "white",
          padding: (theme) => theme.spacing(2),
          "& h2": {
            fontSize: "1.5rem",
            fontWeight: 600,
          },
        }}
      >
        {layerNumber == null ? title : `Add Material to Layer ${layerNumber}`}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: (theme) => theme.spacing(3),
          display: "flex",
          flexDirection: "column",
          gap: (theme) => theme.spacing(3),
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <CommoditySelector
                value={commodityId}
                onChange={handleCommodityChange}
                excludedCommodityIds={
                  layerNumber == null ? [] : getCommodityIdsInLayer(layerNumber)
                }
              />
              {showError && (
                <FormHelperText
                  sx={{
                    color: (theme) => theme.palette.error.main,
                    marginTop: (theme) => theme.spacing(1),
                  }}
                >
                  Commodity selection is required
                </FormHelperText>
              )}
            </Box>
            <TextField
              select
              label="Layer"
              size="small"
              fullWidth
              value={layerNumber ?? NEW_LAYER}
              onChange={(e) =>
                setLayerNumber(
                  e.target.value === NEW_LAYER ? null : Number(e.target.value),
                )
              }
            >
              <MenuItem value={NEW_LAYER}>Layer {newLayerNumber}</MenuItem>
              {layerNumbers.map((number) => (
                <MenuItem
                  key={number}
                  value={number}
                  disabled={
                    commodityId != null &&
                    getCommodityIdsInLayer(number).includes(commodityId)
                  }
                >
                  Layer {number}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: (theme) => theme.spacing(2, 3, 3),
          width: "100%",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              onClick={onClose}
              disabled={isLoading}
              variant="contained"
              sx={{
                padding: (theme) => theme.spacing(1, 3),
                borderRadius: "4px",
                width: "100%",
                backgroundColor: (theme) =>
                  theme.palette.charges.status.inactive,
                color: "white",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[600],
                },
              }}
            >
              CANCEL
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              onClick={handleSave}
              disabled={isLoading || !commodityId}
              variant="contained"
              sx={{
                padding: (theme) => theme.spacing(1, 3),
                borderRadius: "4px",
                width: "100%",
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.primary.dark,
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "SAVE"
              )}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
